import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Table,
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  Space,
  Tag,
  Modal,
  Form,
  message,
  Popconfirm,
  Tooltip,
  Image,
} from 'antd';
import {
  SearchOutlined,
  CheckOutlined,
  CloseOutlined,
  DollarOutlined,
  UserOutlined,
  GlobalOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import {
  fetchRechargeRecords,
  approveRecharge,
  setFilters,
  setPagination,
  clearError,
} from '../../features/finance/financeSlice';
import { formatDateTime, formatAmount, formatAmountWithRegion, maskPhone, getStatusColor, getStatusText } from '../../utils/index';
import { get } from '../../utils/api';

const { RangePicker } = DatePicker;
const { Option } = Select;

const RechargeManagement = () => {
  const dispatch = useDispatch();
  const { rechargeRecords, pagination, filters, isLoading, error } = useSelector((state) => state.finance);
  
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [regions, setRegions] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);

  // 使用 useMemo 计算请求参数
  const requestParams = React.useMemo(() => ({
    ...filters,
    page: pagination.current || 1,
    limit: pagination.pageSize || 20,
  }), [filters, pagination.current, pagination.pageSize]);
  
  // 使用 useRef 防止死循环
  const prevParamsRef = React.useRef('');
  const isLoadingRef = React.useRef(false);
  
  useEffect(() => {
    // 如果正在加载，则不重复请求
    if (isLoadingRef.current) {
      return;
    }
    
    // 深度比较参数，只有真正变化时才请求
    const paramsStr = JSON.stringify(requestParams);
    
    if (paramsStr !== prevParamsRef.current) {
      prevParamsRef.current = paramsStr;
      isLoadingRef.current = true;
      dispatch(fetchRechargeRecords(requestParams)).finally(() => {
        isLoadingRef.current = false;
      });
    }
  }, [dispatch, requestParams]);

  // 监听充值更新事件（由全局通知触发）
  useEffect(() => {
    const handleRechargeUpdated = () => {
      console.log('充值记录已更新，刷新列表');
      dispatch(fetchRechargeRecords(requestParams));
    };

    window.addEventListener('recharge-updated', handleRechargeUpdated);

    return () => {
      window.removeEventListener('recharge-updated', handleRechargeUpdated);
    };
  }, [dispatch, requestParams]);

  useEffect(() => {
    if (error) {
      message.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // 获取地区列表
  useEffect(() => {
    const fetchRegions = async () => {
      try {
        const res = await get('/admin/regions/all');
        if (res.code === 200) {
          const regionList = res.data || [];
          setRegions(regionList);
          // 默认选择中国地区（CN）
          const defaultRegion = regionList.find(r => 
            (r.region_code || r.regionCode) === 'ZMW'
          );
          if (defaultRegion) {
            setSelectedRegion(defaultRegion);
          }
        }
      } catch (error) {
        console.error('获取地区列表失败:', error);
      }
    };
    fetchRegions();
  }, []);

  // 搜索处理
  const handleSearch = (values) => {
    const newFilters = {
      ...values,
      startDate: values.dateRange?.[0]?.format('YYYY-MM-DD'),
      endDate: values.dateRange?.[1]?.format('YYYY-MM-DD'),
    };
    delete newFilters.dateRange;
    dispatch(setFilters(newFilters));
    dispatch(setPagination({ current: 1 }));
  };

  // 重置搜索
  const handleReset = () => {
    form.resetFields();
    dispatch(setFilters({}));
    dispatch(setPagination({ current: 1 }));
  };

  // 根据充值方式渲染详细信息
  const renderPaymentDetails = (record) => {
    const method = record.paymentMethod;
    
    switch (method) {
      case 'bank_transfer':
      case 'bank':
        return (
          <Space direction="vertical" size={2}>
            {record.userBankName && (
              <span style={{ fontSize: '12px' }}>
                银行: {record.userBankName}
              </span>
            )}
            {record.userBankAccount && (
              <span style={{ fontSize: '12px' }}>
                账号: ****{record.userBankAccount}
              </span>
            )}
            {record.platformBankName && (
              <span style={{ fontSize: '12px', color: '#999' }}>
                收款行: {record.platformBankName}
              </span>
            )}
          </Space>
        );
      
      case 'alipay':
        return (
          <Space direction="vertical" size={2}>
            {record.userAlipayAccount && (
              <span style={{ fontSize: '12px' }}>
                账号: {record.userAlipayAccount}
              </span>
            )}
            {record.platformAlipayAccount && (
              <span style={{ fontSize: '12px', color: '#999' }}>
                收款账号: {record.platformAlipayAccount}
              </span>
            )}
          </Space>
        );
      
      case 'wechat':
        return (
          <Space direction="vertical" size={2}>
            {record.userWechatAccount && (
              <span style={{ fontSize: '12px' }}>
                账号: {record.userWechatAccount}
              </span>
            )}
            {record.platformWechatAccount && (
              <span style={{ fontSize: '12px', color: '#999' }}>
                收款账号: {record.platformWechatAccount}
              </span>
            )}
          </Space>
        );
      
      case 'usdt':
        return (
          <Space direction="vertical" size={2}>
            {record.usdtNetwork && (
              <Tag color="purple" style={{ fontSize: '11px' }}>
                {record.usdtNetwork}
              </Tag>
            )}
            {record.usdtAddress && (
              <span style={{ fontSize: '11px', fontFamily: 'monospace' }}>
                {record.usdtAddress.substring(0, 10)}...{record.usdtAddress.substring(record.usdtAddress.length - 8)}
              </span>
            )}
            {record.usdtTxid && (
              <Tooltip title={record.usdtTxid}>
                <span style={{ fontSize: '11px', color: '#1890ff', cursor: 'pointer' }}>
                  TxID: {record.usdtTxid.substring(0, 8)}...
                </span>
              </Tooltip>
            )}
          </Space>
        );
      
      case 'm-pesa':
        return (
          <Space direction="vertical" size={2}>
            {record.mpesaPhone && (
              <span style={{ fontSize: '12px' }}>
                手机: {record.mpesaPhone}
              </span>
            )}
            {record.mpesaName && (
              <span style={{ fontSize: '12px' }}>
                姓名: {record.mpesaName}
              </span>
            )}
            {record.platformMpesaPhone && (
              <span style={{ fontSize: '12px', color: '#999' }}>
                收款号: {record.platformMpesaPhone}
              </span>
            )}
          </Space>
        );
      
      default:
        return <span style={{ color: '#999' }}>-</span>;
    }
  };

  // 表格列配置
  const columns = [
    {
      title: '充值ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '用户信息',
      dataIndex: 'userName',
      key: 'userName',
      render: (name, record) => (
        <Space>
          <UserOutlined />
          <span>{record.userPhone}</span>
          <span style={{ color: '#8c8c8c', fontSize: '12px' }}>
            ({name})
          </span>
        </Space>
      ),
    },
    {
      title: '充值金额',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount) => (
        <Space direction="vertical" size={0}>
          <Space>
            <DollarOutlined />
            <span style={{ fontWeight: 'bold', color: '#52c41a' }}>
              {selectedRegion ? formatAmountWithRegion(amount, selectedRegion) : formatAmount(amount)}
            </span>
          </Space>
          {/* {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
            <span style={{ fontSize: '12px', color: '#999' }}>
              原始: {formatAmount(amount)}
            </span>
          )} */}
        </Space>
      ),
    },
    {
      title: '充值方式',
      dataIndex: 'paymentMethod',
      key: 'paymentMethod',
      render: (method) => {
const methodMap = {
          bank_transfer: { text: '银行卡', color: 'blue' },
          bank: { text: '银行卡', color: 'blue' },
          alipay: { text: '支付宝', color: 'green' },
          wechat: { text: '微信', color: 'green' },
          usdt: { text: 'USDT', color: 'orange' },
          'm-pesa': { text: 'M-Pesa', color: 'cyan' },
          mobile_money: { text: 'Mobile Money', color: 'purple' },
        };
        const config = methodMap[method] || { text: method, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '支付详情',
      key: 'paymentDetails',
      width: 200,
      render: (_, record) => renderPaymentDetails(record),
    },
    {
      title: '充值状态',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '审核状态',
      dataIndex: 'auditStatus',
      key: 'auditStatus',
      render: (auditStatus) => {
        const statusMap = {
          pending: { text: '待审核', color: 'orange' },
          approved: { text: '已通过', color: 'green' },
          rejected: { text: '已拒绝', color: 'red' },
        };
        const config = statusMap[auditStatus] || { text: auditStatus, color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
    {
      title: '支付凭证',
      dataIndex: 'paymentVoucher',
      key: 'paymentVoucher',
      width: 120,
      render: (voucher) => {
        if (!voucher) {
          return <span style={{ color: '#999' }}>无</span>;
        }
        
        // 处理图片URL
        let imageUrl = voucher;
        // 如果是绝对路径（http:// 或 https://），直接使用
        // 如果是相对路径（以 / 开头），直接使用
        // 如果只是文件名（不包含路径），拼接完整路径
        if (!voucher.startsWith('http://') && !voucher.startsWith('https://') && !voucher.startsWith('/')) {
          // 如果既不是绝对路径也不是以 / 开头的相对路径，可能是文件名
          imageUrl = `/api/v1/files/voucher/${voucher}`;
        }
        
        return (
          <Tooltip title="点击查看大图">
            <Image
              width={80}
              height={80}
              src={imageUrl}
              alt="支付凭证"
              preview={{
                mask: '查看大图',
              }}
              style={{
                objectFit: 'cover',
                cursor: 'pointer',
                borderRadius: '4px',
                border: '1px solid #e8e8e8',
              }}
              fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
            />
          </Tooltip>
        );
      },
    },
    {
      title: '申请时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDateTime(date),
    },
    {
      title: '到账时间',
      dataIndex: 'completedAt',
      key: 'completedAt',
      render: (date) => date ? formatDateTime(date) : '-',
    },
    {
      title: '操作',
      key: 'action',
      width: 200,
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="查看详情">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handleView(record)}
            >
              查看
            </Button>
          </Tooltip>
          {record.auditStatus === 'pending' && (
            <>
              <Popconfirm
                title="确认通过"
                description={`确认通过用户 ${record.userName} 的充值申请？`}
                onConfirm={() => handleApprove(record, true)}
                okText="确认"
                cancelText="取消"
              >
                <Tooltip title="通过">
                  <Button
                    type="primary"
                    size="small"
                    icon={<CheckOutlined />}
                    style={{ color: '#fff' }}
                  >
                    通过
                  </Button>
                </Tooltip>
              </Popconfirm>
              <Popconfirm
                title="确认拒绝"
                description={`确认拒绝用户 ${record.userName} 的充值申请？`}
                onConfirm={() => handleApprove(record, false)}
                okText="确认"
                cancelText="取消"
                okButtonProps={{ danger: true }}
              >
                <Tooltip title="拒绝">
                  <Button
                    danger
                    size="small"
                    icon={<CloseOutlined />}
                  >
                    拒绝
                  </Button>
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  // 查看详情
  const handleView = (record) => {
    setSelectedRecord(record);
    setViewModalVisible(true);
  };

  // 审核充值
  const handleApprove = (record, approved) => {
    setSelectedRecord({ ...record, approved });
    setApproveModalVisible(true);
  };

  // 确认审核
  const handleConfirmApprove = async (values) => {
    try {
      await dispatch(approveRecharge({
        recordId: selectedRecord.id,
        approved: values.approved ?? selectedRecord.approved,
        remark: values.remark,
      })).unwrap();
      message.success(values.approved ?? selectedRecord.approved ? '充值审核通过' : '充值审核拒绝');
      setApproveModalVisible(false);
      dispatch(fetchRechargeRecords(requestParams));
    } catch (error) {
      message.error(error);
    }
  };

  // 表格选择
  const rowSelection = {
    selectedRowKeys,
    onChange: setSelectedRowKeys,
  };

  // 分页处理
  const handleTableChange = (pagination) => {
    dispatch(setPagination({
      current: pagination.current,
      pageSize: pagination.pageSize,
    }));
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">充值管理</h1>
        <p className="page-description">管理用户充值申请和审核</p>
      </div>

      <Card>
        {/* 地区选择器 */}
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: '8px' ,display:'none' }}>
          <GlobalOutlined style={{ fontSize: '16px', color: '#1890ff' }} />
          <span style={{ fontWeight: 500 }}>显示货币：</span>
          <Select
            style={{ width: 360 }}
            value={selectedRegion ? (selectedRegion.region_code || selectedRegion.regionCode) : undefined}
            onChange={(value) => {
              const region = regions.find(r => 
                (r.region_code || r.regionCode) === value
              );
              setSelectedRegion(region);
            }}
            placeholder="选择地区"
          >
            {regions.map(region => {
              const regionCode = region.region_code || region.regionCode;
              const regionName = region.region_name || region.regionName;
              const currencySymbol = region.currency_symbol || region.currencySymbol;
              const currencyName = region.currency_name || region.currencyName;
              const exchangeRate = region.exchange_rate || region.exchangeRate || 1;
              
              return (
                <Option key={regionCode} value={regionCode}>
                  <Space>
                    <Tag color="cyan">{regionCode}</Tag>
                    <span>{regionName}</span>
                    <Tag color="green">{currencySymbol} {currencyName}</Tag>
                    {exchangeRate !== 1 && (
                      <span style={{ fontSize: '12px', color: '#999' }}>
                        (汇率: {exchangeRate})
                      </span>
                    )}
                  </Space>
                </Option>
              );
            })}
          </Select>
          {selectedRegion && (
            <Tag color="blue">
              当前: {selectedRegion.currency_symbol || selectedRegion.currencySymbol} {selectedRegion.currency_name || selectedRegion.currencyName}
            </Tag>
          )}
        </div>

        {/* 搜索表单 */}
        <Form
          form={form}
          layout="inline"
          onFinish={handleSearch}
          style={{ marginBottom: 16 }}
        >
          <Form.Item name="keyword">
            <Input
              placeholder="搜索用户手机号/姓名"
              prefix={<SearchOutlined />}
              style={{ width: 200 }}
            />
          </Form.Item>
          <Form.Item name="auditStatus">
            <Select placeholder="审核状态" style={{ width: 120 }} allowClear>
              <Option value="pending">待审核</Option>
              <Option value="approved">已通过</Option>
              <Option value="rejected">已拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item name="status">
            <Select placeholder="充值状态" style={{ width: 120 }} allowClear>
              <Option value="pending">待处理</Option>
              <Option value="processing">处理中</Option>
              <Option value="completed">已完成</Option>
              <Option value="failed">失败</Option>
              <Option value="cancelled">已取消</Option>
            </Select>
          </Form.Item>
<Form.Item name="paymentMethod">
            <Select placeholder="充值方式" style={{ width: 120 }} allowClear>
              <Option value="bank">银行卡</Option>
              <Option value="alipay">支付宝</Option>
              <Option value="wechat">微信</Option>
              <Option value="usdt">USDT</Option>
              <Option value="m-pesa">m-pesa</Option>
              <Option value="mobile_money">mobile_money</Option>
            </Select>
          </Form.Item>
          <Form.Item name="dateRange">
            <RangePicker placeholder={['开始日期', '结束日期']} />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>
                搜索
              </Button>
              <Button onClick={handleReset}>重置</Button>
            </Space>
          </Form.Item>
        </Form>

        {/* 操作按钮 */}
        <div className="table-toolbar">
          <Space>
            <Button
              type="primary"
              disabled={selectedRowKeys.length === 0}
              icon={<CheckOutlined />}
            >
              批量通过
            </Button>
            <Button
              danger
              disabled={selectedRowKeys.length === 0}
              icon={<CloseOutlined />}
            >
              批量拒绝
            </Button>
          </Space>
          <div>
            共 {pagination.total} 条充值记录
          </div>
        </div>

        {/* 充值记录表格 */}
        <Table
          columns={columns}
          dataSource={rechargeRecords}
          rowKey="id"
          loading={isLoading}
          rowSelection={rowSelection}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total: pagination.total,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) =>
              `第 ${range[0]}-${range[1]} 条/共 ${total} 条`,
          }}
          onChange={handleTableChange}
        />
      </Card>

      {/* 查看详情模态框 */}
      <Modal
        title="充值详情"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={
          <Button onClick={() => setViewModalVisible(false)}>
            关闭
          </Button>
        }
        width={700}
      >
        <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
          <Space direction="vertical" size={16} style={{ width: '100%' }}>
            {/* 基本信息 */}
            <div>
              <h3 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600, color: '#262626' }}>基本信息</h3>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>充值ID:</span>
                  <span style={{ fontWeight: 500 }}>{selectedRecord?.id}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>充值编号:</span>
                  <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>{selectedRecord?.rechargeNo}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>用户信息:</span>
                  <span>{selectedRecord?.userName} ({selectedRecord?.userPhone})</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>充值金额:</span>
                  <span style={{ fontWeight: 'bold', color: '#52c41a', fontSize: '16px' }}>
                    {selectedRegion ? formatAmountWithRegion(selectedRecord?.amount, selectedRegion) : formatAmount(selectedRecord?.amount)}
                  </span>
                </div>
<div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>充值方式:</span>
                  <Tag color={
                    selectedRecord?.paymentMethod === 'bank' || selectedRecord?.paymentMethod === 'bank_transfer' ? 'blue' :
                    selectedRecord?.paymentMethod === 'alipay' ? 'green' :
                    selectedRecord?.paymentMethod === 'wechat' ? 'green' :
                    selectedRecord?.paymentMethod === 'usdt' ? 'orange' :
                    selectedRecord?.paymentMethod === 'm-pesa' ? 'cyan' :
                    selectedRecord?.paymentMethod === 'mobile_money' ? 'purple' : 'default'
                  }>
                    {selectedRecord?.paymentMethod}
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>充值状态:</span>
                  <Tag color={getStatusColor(selectedRecord?.status)}>
                    {getStatusText(selectedRecord?.status)}
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>审核状态:</span>
                  <Tag color={
                    selectedRecord?.auditStatus === 'pending' ? 'orange' :
                    selectedRecord?.auditStatus === 'approved' ? 'green' :
                    selectedRecord?.auditStatus === 'rejected' ? 'red' : 'default'
                  }>
                    {selectedRecord?.auditStatus === 'pending' ? '待审核' :
                     selectedRecord?.auditStatus === 'approved' ? '已通过' :
                     selectedRecord?.auditStatus === 'rejected' ? '已拒绝' : selectedRecord?.auditStatus}
                  </Tag>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#8c8c8c' }}>申请时间:</span>
                  <span>{formatDateTime(selectedRecord?.createdAt)}</span>
                </div>
                {selectedRecord?.completedAt && (
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8c8c8c' }}>到账时间:</span>
                    <span>{formatDateTime(selectedRecord?.completedAt)}</span>
                  </div>
                )}
              </Space>
            </div>

            {/* 支付详情 */}
            <div>
              <h3 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600, color: '#262626' }}>支付详情</h3>
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                {/* 银行卡 */}
                {(selectedRecord?.paymentMethod === 'bank_transfer' || selectedRecord?.paymentMethod === 'bank') && (
                  <>
                    {selectedRecord?.userBankName && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户银行:</span>
                        <span>{selectedRecord.userBankName}</span>
                      </div>
                    )}
                    {selectedRecord?.userBankAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户账号:</span>
                        <span>****{selectedRecord.userBankAccount}</span>
                      </div>
                    )}
                    {selectedRecord?.platformBankName && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款银行:</span>
                        <span>{selectedRecord.platformBankName}</span>
                      </div>
                    )}
                    {selectedRecord?.platformBankAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款账号:</span>
                        <span>{selectedRecord.platformBankAccount}</span>
                      </div>
                    )}
                    {selectedRecord?.platformBankAccountHolder && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款户名:</span>
                        <span>{selectedRecord.platformBankAccountHolder}</span>
                      </div>
                    )}
                  </>
                )}

                {/* 支付宝 */}
                {selectedRecord?.paymentMethod === 'alipay' && (
                  <>
                    {selectedRecord?.userAlipayAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户支付宝:</span>
                        <span>{selectedRecord.userAlipayAccount}</span>
                      </div>
                    )}
                    {selectedRecord?.platformAlipayAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款支付宝:</span>
                        <span>{selectedRecord.platformAlipayAccount}</span>
                      </div>
                    )}
                  </>
                )}

                {/* 微信 */}
                {selectedRecord?.paymentMethod === 'wechat' && (
                  <>
                    {selectedRecord?.userWechatAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户微信:</span>
                        <span>{selectedRecord.userWechatAccount}</span>
                      </div>
                    )}
                    {selectedRecord?.platformWechatAccount && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款微信:</span>
                        <span>{selectedRecord.platformWechatAccount}</span>
                      </div>
                    )}
                  </>
                )}

                {/* USDT */}
                {selectedRecord?.paymentMethod === 'usdt' && (
                  <>
                    {selectedRecord?.usdtNetwork && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>USDT网络:</span>
                        <Tag color="purple">{selectedRecord.usdtNetwork}</Tag>
                      </div>
                    )}
                    {selectedRecord?.usdtAddress && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ color: '#8c8c8c' }}>用户USDT地址:</span>
                        <Input.TextArea 
                          value={selectedRecord.usdtAddress} 
                          readOnly
                          rows={2}
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </div>
                    )}
                    {selectedRecord?.usdtTxid && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ color: '#8c8c8c' }}>交易哈希(TxID):</span>
                        <Input.TextArea 
                          value={selectedRecord.usdtTxid} 
                          readOnly
                          rows={2}
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </div>
                    )}
                    {selectedRecord?.platformUsdtAddress && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款USDT地址:</span>
                        <Input.TextArea 
                          value={selectedRecord.platformUsdtAddress} 
                          readOnly
                          rows={2}
                          style={{ fontFamily: 'monospace', fontSize: '12px' }}
                        />
                      </div>
                    )}
                  </>
                )}

{/* M-Pesa */}
                {selectedRecord?.paymentMethod === 'm-pesa' && (
                  <>
                    {selectedRecord?.mpesaPhone && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户M-Pesa手机:</span>
                        <span>{selectedRecord.mpesaPhone}</span>
                      </div>
                    )}
                    {selectedRecord?.mpesaName && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户M-Pesa姓名:</span>
                        <span>{selectedRecord.mpesaName}</span>
                      </div>
                    )}
                    {selectedRecord?.platformMpesaPhone && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款M-Pesa号:</span>
                        <span>{selectedRecord.platformMpesaPhone}</span>
                      </div>
                    )}
                    {selectedRecord?.platformMpesaName && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款M-Pesa姓名:</span>
                        <span>{selectedRecord.platformMpesaName}</span>
                      </div>
                    )}
                  </>
                )}

                {/* Mobile Money */}
                {selectedRecord?.paymentMethod === 'mobile_money' && (
                  <>
                    {selectedRecord?.mpesaPhone && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户Mobile Money手机:</span>
                        <span>{selectedRecord.mpesaPhone}</span>
                      </div>
                    )}
                    {selectedRecord?.mpesaName && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>用户Mobile Money姓名:</span>
                        <span>{selectedRecord.mpesaName}</span>
                      </div>
                    )}
                    {selectedRecord?.platformMpesaPhone && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款Mobile Money号:</span>
                        <span>{selectedRecord.platformMpesaPhone}</span>
                      </div>
                    )}
                    {selectedRecord?.platformMpesaName && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#8c8c8c' }}>平台收款Mobile Money姓名:</span>
                        <span>{selectedRecord.platformMpesaName}</span>
                      </div>
                    )}
                  </>
                )}
              </Space>
            </div>

            {/* 支付凭证 */}
            {selectedRecord?.paymentVoucher && (
              <div>
                <h3 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600, color: '#262626' }}>支付凭证</h3>
                <Image
                  width={200}
                  src={
                    selectedRecord.paymentVoucher.startsWith('http://') || 
                    selectedRecord.paymentVoucher.startsWith('https://') || 
                    selectedRecord.paymentVoucher.startsWith('/') 
                      ? selectedRecord.paymentVoucher 
                      : `/api/v1/files/voucher/${selectedRecord.paymentVoucher}`
                  }
                  alt="支付凭证"
                  style={{ borderRadius: '4px', border: '1px solid #e8e8e8' }}
                  fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNkYPhfDwAChwGA60e6kgAAAABJRU5ErkJggg=="
                />
              </div>
            )}

            {/* 用户备注 */}
            {selectedRecord?.remark && (
              <div>
                <h3 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600, color: '#262626' }}>用户备注</h3>
                <div style={{ 
                  padding: '12px', 
                  background: '#fafafa', 
                  borderRadius: '4px',
                  border: '1px solid #e8e8e8',
                  color: '#595959'
                }}>
                  {selectedRecord.remark}
                </div>
              </div>
            )}

            {/* 审核信息 */}
            {(selectedRecord?.auditStatus === 'approved' || selectedRecord?.auditStatus === 'rejected') && (
              <div>
                <h3 style={{ marginBottom: 12, fontSize: '14px', fontWeight: 600, color: '#262626' }}>审核信息</h3>
                <Space direction="vertical" size={8} style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#8c8c8c' }}>审核结果:</span>
                    <Tag color={selectedRecord?.auditStatus === 'approved' ? 'green' : 'red'}>
                      {selectedRecord?.auditStatus === 'approved' ? '已通过' : '已拒绝'}
                    </Tag>
                  </div>
                  {selectedRecord?.auditRemark && (
                    <div>
                      <span style={{ color: '#8c8c8c', display: 'block', marginBottom: '8px' }}>审核备注:</span>
                      <div style={{ 
                        padding: '12px', 
                        background: selectedRecord?.auditStatus === 'approved' ? '#f6ffed' : '#fff2f0', 
                        borderRadius: '4px',
                        border: `1px solid ${selectedRecord?.auditStatus === 'approved' ? '#b7eb8f' : '#ffccc7'}`,
                        color: '#595959'
                      }}>
                        {selectedRecord.auditRemark}
                      </div>
                    </div>
                  )}
                </Space>
              </div>
            )}
          </Space>
        </div>
      </Modal>

      {/* 审核模态框 */}
      <Modal
        title="审核充值"
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        footer={null}
        width={600}
      >
        <Form
          layout="vertical"
          onFinish={handleConfirmApprove}
        >
          <Form.Item label="用户信息">
            <Input value={`${selectedRecord?.userName} (${maskPhone(selectedRecord?.userPhone)})`} disabled />
          </Form.Item>
          <Form.Item label="充值金额">
            <Space direction="vertical" size={4} style={{ width: '100%' }}>
              <Input 
                value={selectedRegion ? formatAmountWithRegion(selectedRecord?.amount, selectedRegion) : formatAmount(selectedRecord?.amount)} 
                disabled 
              />
              {selectedRegion && (selectedRegion.region_code || selectedRegion.regionCode) !== 'CN' && (
                <span style={{ fontSize: '12px', color: '#999' }}>
                  原始金额: {formatAmount(selectedRecord?.amount)}
                </span>
              )}
            </Space>
          </Form.Item>
          <Form.Item label="充值方式">
            <Input value={selectedRecord?.paymentMethod} disabled />
          </Form.Item>
          
          {/* 根据充值方式显示不同的详细信息 */}
          {selectedRecord?.paymentMethod === 'bank_transfer' || selectedRecord?.paymentMethod === 'bank' ? (
            <>
              {selectedRecord?.userBankName && (
                <Form.Item label="用户银行">
                  <Input value={selectedRecord.userBankName} disabled />
                </Form.Item>
              )}
              {selectedRecord?.userBankAccount && (
                <Form.Item label="用户账号">
                  <Input value={`****${selectedRecord.userBankAccount}`} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformBankName && (
                <Form.Item label="平台收款银行">
                  <Input value={selectedRecord.platformBankName} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformBankAccount && (
                <Form.Item label="平台收款账号">
                  <Input value={selectedRecord.platformBankAccount} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformBankAccountHolder && (
                <Form.Item label="平台收款户名">
                  <Input value={selectedRecord.platformBankAccountHolder} disabled />
                </Form.Item>
              )}
            </>
          ) : null}

          {selectedRecord?.paymentMethod === 'alipay' ? (
            <>
              {selectedRecord?.userAlipayAccount && (
                <Form.Item label="用户支付宝账号">
                  <Input value={selectedRecord.userAlipayAccount} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformAlipayAccount && (
                <Form.Item label="平台收款支付宝">
                  <Input value={selectedRecord.platformAlipayAccount} disabled />
                </Form.Item>
              )}
            </>
          ) : null}

          {selectedRecord?.paymentMethod === 'wechat' ? (
            <>
              {selectedRecord?.userWechatAccount && (
                <Form.Item label="用户微信账号">
                  <Input value={selectedRecord.userWechatAccount} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformWechatAccount && (
                <Form.Item label="平台收款微信">
                  <Input value={selectedRecord.platformWechatAccount} disabled />
                </Form.Item>
              )}
            </>
          ) : null}

          {selectedRecord?.paymentMethod === 'usdt' ? (
            <>
              {selectedRecord?.usdtNetwork && (
                <Form.Item label="USDT网络">
                  <Input value={selectedRecord.usdtNetwork} disabled />
                </Form.Item>
              )}
              {selectedRecord?.usdtAddress && (
                <Form.Item label="用户USDT地址">
                  <Input.TextArea 
                    value={selectedRecord.usdtAddress} 
                    disabled 
                    rows={2}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                </Form.Item>
              )}
              {selectedRecord?.usdtTxid && (
                <Form.Item label="交易哈希(TxID)">
                  <Input.TextArea 
                    value={selectedRecord.usdtTxid} 
                    disabled 
                    rows={2}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                </Form.Item>
              )}
              {selectedRecord?.platformUsdtAddress && (
                <Form.Item label="平台收款USDT地址">
                  <Input.TextArea 
                    value={selectedRecord.platformUsdtAddress} 
                    disabled 
                    rows={2}
                    style={{ fontFamily: 'monospace', fontSize: '12px' }}
                  />
                </Form.Item>
              )}
            </>
          ) : null}

{selectedRecord?.paymentMethod === 'm-pesa' ? (
            <>
              {selectedRecord?.mpesaPhone && (
                <Form.Item label="用户M-Pesa手机号">
                  <Input value={selectedRecord.mpesaPhone} disabled />
                </Form.Item>
              )}
              {selectedRecord?.mpesaName && (
                <Form.Item label="用户M-Pesa姓名">
                  <Input value={selectedRecord.mpesaName} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformMpesaPhone && (
                <Form.Item label="平台收款M-Pesa号">
                  <Input value={selectedRecord.platformMpesaPhone} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformMpesaName && (
                <Form.Item label="平台收款M-Pesa姓名">
                  <Input value={selectedRecord.platformMpesaName} disabled />
                </Form.Item>
              )}
            </>
          ) : null}

          {selectedRecord?.paymentMethod === 'mobile_money' ? (
            <>
              {selectedRecord?.mpesaPhone && (
                <Form.Item label="用户Mobile Money手机号">
                  <Input value={selectedRecord.mpesaPhone} disabled />
                </Form.Item>
              )}
              {selectedRecord?.mpesaName && (
                <Form.Item label="用户Mobile Money姓名">
                  <Input value={selectedRecord.mpesaName} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformMpesaPhone && (
                <Form.Item label="平台收款Mobile Money号">
                  <Input value={selectedRecord.platformMpesaPhone} disabled />
                </Form.Item>
              )}
              {selectedRecord?.platformMpesaName && (
                <Form.Item label="平台收款Mobile Money姓名">
                  <Input value={selectedRecord.platformMpesaName} disabled />
                </Form.Item>
              )}
            </>
          ) : null}

          {selectedRecord?.remark && (
            <Form.Item label="用户备注">
              <Input.TextArea value={selectedRecord.remark} disabled rows={2} />
            </Form.Item>
          )}

          <Form.Item
            label="审核结果"
            name="approved"
            initialValue={selectedRecord?.approved}
          >
            <Select placeholder="请选择审核结果" disabled>
              <Option value={true}>通过</Option>
              <Option value={false}>拒绝</Option>
            </Select>
          </Form.Item>
          <Form.Item
            label="审核备注"
            name="remark"
            rules={[{ required: true, message: '请输入审核备注' }]}
          >
            <Input.TextArea
              rows={3}
              placeholder="请输入审核备注"
            />
          </Form.Item>
          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                确认审核
              </Button>
              <Button onClick={() => setApproveModalVisible(false)}>
                取消
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default RechargeManagement;
